import React from 'react';
import clsx from 'clsx';
import { FiCheck, FiX } from 'react-icons/fi';

import { Box, Flex, Stack, Text } from '@highlight-cp/core-ui';

import classes from './toggleable-list.module.css';

export interface ToggleableListItem {
  value: string;
  label: string;
}

export interface ToggleableListProps {
  items: ToggleableListItem[];
  selectedValues?: string[];
  onChange: (selectedValues: string[]) => void;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

const ICON_PROPS = {
  size: 20,
  strokeWidth: 1.5,
} as const;

/**
 * ### ToggleableList
 *
 * @description
 * A list of toggleable items that can be selected/deselected. Selected items show
 * a green checkmark, while unselected items show a gray X icon with "Excluded" label.
 *
 * @example
 * <ToggleableList
 *   items={[
 *     { value: 'freq', label: 'Expected Usage Frequency' },
 *     { value: 'occasions', label: 'Other Occasions' }
 *   ]}
 *   selectedValues={['freq']}
 *   onChange={(values) => console.log('Selected:', values)}
 * />
 *
 * @param props - Component props
 * @returns React component
 */
export const ToggleableList: React.FC<ToggleableListProps> = ({
  items,
  selectedValues = [],
  onChange,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}) => {
  const handleToggle = (value: string) => {
    const isSelected = selectedValues.includes(value);
    const newSelectedValues = isSelected
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];

    onChange(newSelectedValues);
  };

  return (
    <Stack gap={4} role="group" aria-label={ariaLabel} aria-labelledby={ariaLabelledBy}>
      {items.map((item) => {
        const isSelected = selectedValues.includes(item.value);

        return (
          <button
            key={item.value}
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleToggle(item.value);
            }}
            className={clsx(classes.toggleButton, !isSelected && classes.toggleButtonExcluded)}
            aria-pressed={isSelected}
            aria-label={`${isSelected ? 'Remove' : 'Add'} ${item.label}`}
          >
            <Flex align="center" justify="space-between" px={4} py={6}>
              <Flex align="center" className={classes.labelContainer}>
                <Box className={classes.iconContainer}>
                  {isSelected ? (
                    <FiCheck {...ICON_PROPS} className={classes.iconCheck} />
                  ) : (
                    <FiX {...ICON_PROPS} className={classes.iconX} />
                  )}
                </Box>
                {isSelected ? (
                  <Text lineClamp={2}>{item.label}</Text>
                ) : (
                  <Text size="sm" c="dimmed" lineClamp={2}>
                    {item.label}
                  </Text>
                )}
              </Flex>
              {!isSelected && (
                <span className={classes.excludedText}>
                  <Text size="sm">Excluded</Text>
                </span>
              )}
            </Flex>
          </button>
        );
      })}
    </Stack>
  );
};

export default ToggleableList;
